import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const DEFAULT_COOLDOWN_SECONDS = 3;

const EXPORT_BUTTON_LABEL_KEY = 'components.asyncReportDownload.exportButtonLabel';
const EXPORT_BUTTON_WITH_COOLDOWN_KEY = 'components.asyncReportDownload.exportButtonWithCooldown';

/**
 * 异步报表导出：最短锁定 + 倒计时文案，防止连点。
 * 在请求结束后再补足至 totalSeconds，保证按钮至少禁用 totalSeconds 秒。
 */
export function useAsyncReportExportCooldown(totalSeconds: number = DEFAULT_COOLDOWN_SECONDS) {
    const { t } = useTranslation();
    const [exportLoading, setExportLoading] = useState(false);
    const [cooldownTick, setCooldownTick] = useState<number | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const mountedRef = useRef(true);
    const paddingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const paddingResolveRef = useRef<(() => void) | null>(null);

    const stopInterval = useCallback(() => {
        if (intervalRef.current != null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const clearPaddingWait = useCallback(() => {
        if (paddingTimeoutRef.current != null) {
            clearTimeout(paddingTimeoutRef.current);
            paddingTimeoutRef.current = null;
        }
        const finish = paddingResolveRef.current;
        paddingResolveRef.current = null;
        finish?.();
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            stopInterval();
            clearPaddingWait();
        };
    }, [stopInterval, clearPaddingWait]);

    const runExportWithCooldown = useCallback(
        async (exportWork: () => Promise<void>) => {
            if (exportLoading) return;
            if (!mountedRef.current) return;
            setExportLoading(true);
            setCooldownTick(totalSeconds);
            let remaining = totalSeconds;
            intervalRef.current = setInterval(() => {
                if (!mountedRef.current) {
                    stopInterval();
                    return;
                }
                remaining -= 1;
                if (remaining <= 0) {
                    stopInterval();
                    if (mountedRef.current) {
                        setCooldownTick(null);
                    }
                } else if (mountedRef.current) {
                    setCooldownTick(remaining);
                }
            }, 1000);

            const startedAt = Date.now();
            try {
                await exportWork();
            } finally {
                if (!mountedRef.current) {
                    stopInterval();
                    clearPaddingWait();
                    return;
                }
                const elapsed = Date.now() - startedAt;
                const delay = Math.max(0, totalSeconds * 1000 - elapsed);
                await new Promise<void>((resolve) => {
                    paddingResolveRef.current = resolve;
                    if (delay <= 0) {
                        paddingResolveRef.current = null;
                        resolve();
                        return;
                    }
                    paddingTimeoutRef.current = setTimeout(() => {
                        paddingTimeoutRef.current = null;
                        paddingResolveRef.current = null;
                        resolve();
                    }, delay);
                });
                stopInterval();
                if (mountedRef.current) {
                    setCooldownTick(null);
                    setExportLoading(false);
                }
            }
        },
        [exportLoading, totalSeconds, stopInterval, clearPaddingWait]
    );

    const getExportButtonLabel = useCallback(
        (idleLabel?: unknown) => {
            const resolvedIdle =
                idleLabel === undefined || idleLabel === null
                    ? String(t(EXPORT_BUTTON_LABEL_KEY))
                    : String(idleLabel);
            if (exportLoading && cooldownTick != null && cooldownTick > 0) {
                return String(
                    t(EXPORT_BUTTON_WITH_COOLDOWN_KEY, {
                        seconds: cooldownTick,
                        label: resolvedIdle,
                    })
                );
            }
            return resolvedIdle;
        },
        [exportLoading, cooldownTick, t]
    );

    return { exportLoading, runExportWithCooldown, getExportButtonLabel };
}
